import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Field, Textarea } from '@aions/ui-primitives';
import { apiFetch } from '../lib/auth-client';
import { bytesToBase64 } from '../lib/bytes';
import { CartaRecursoPreview } from '../components/CartaRecursoPreview';

type Kind = 'manter' | 'reformar' | 'reformar_parcial';

interface CaseSummary {
  caseId: string;
  recurso: {
    pareceres: ReadonlyArray<{ memberId: string }>;
  } | null;
  state: { kind: string; prazoFimEpochMs?: number };
}

const QUORUM_MIN = 2;
const QUORUM_MAX = 3;

export function DecisaoRecursoPage({
  caseId,
  onBack,
  onDone,
}: {
  caseId: string;
  onBack: () => void;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [kind, setKind] = useState<Kind>('manter');
  const [justificativa, setJustificativa] = useState('');
  const [carta, setCarta] = useState('');
  const [denunciado, setDenunciado] = useState('');
  const [pareceresLen, setPareceresLen] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Carrega estado do caso para ter o contador de pareceres do D-119.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiFetch(`/api/recurso/cases/${caseId}`);
        if (cancelled) return;
        if (resp.ok) {
          const data = (await resp.json()) as CaseSummary;
          setPareceresLen(data.recurso?.pareceres.length ?? 0);
        } else {
          setPareceresLen(0);
        }
      } catch {
        if (!cancelled) setPareceresLen(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const quorumOk = (pareceresLen ?? 0) >= QUORUM_MIN;

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!quorumOk) {
      setError('pareceres_insuficientes');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Placeholder cipher (vide ParecerRecursoPage) — Phase 6 wire crypto real
      // via case-encrypt.encryptForRecurso(text, dekRaw, caseId).
      const justCipher = bytesToBase64(new TextEncoder().encode(justificativa));
      const justNonce = bytesToBase64(new Uint8Array(12));
      const cartaCipher = bytesToBase64(new TextEncoder().encode(carta));
      const cartaNonce = bytesToBase64(new Uint8Array(12));

      const resp = await apiFetch(`/api/recurso/cases/${caseId}/decisao`, {
        method: 'POST',
        body: JSON.stringify({
          kind,
          justificativaPlaintext: justCipher,
          nonce: justNonce,
          cartaPlaintextDenunciante: cartaCipher,
          cartaNonceDenunciante: cartaNonce,
          cartaParaDenunciado: denunciado,
        }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? 'unknown');
        return;
      }
      setSuccess(true);
    } catch {
      setError('network');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 16 }}>
          ← {t('case_detail.back')}
        </Button>
        <Card>
          <Alert variant="success" role="status" style={{ marginBottom: 16 }}>
            {t('decisao.submitted')}
          </Alert>
          <Button onClick={onDone}>{t('case_detail.back')}</Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 16 }}>
        ← {t('case_detail.back')}
      </Button>
      <Card style={{ maxWidth: 800 }}>
        <h1>{t('decisao.title')}</h1>

        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Badge
            tone={quorumOk ? 'success' : 'amber'}
            aria-label={t('decisao.quorum_counter', { count: pareceresLen ?? 0, max: QUORUM_MAX })}
          >
            {t('decisao.quorum_counter', { count: pareceresLen ?? 0, max: QUORUM_MAX })}
          </Badge>
          {!quorumOk && (
            <span style={{ color: 'var(--color-warning)', fontSize: '0.875rem' }}>
              {t('decisao.quorum_min')}
            </span>
          )}
        </div>

        {error && (
          <Alert variant="error" style={{ marginBottom: 16 }}>
            {t(`decisao.errors.${error}`, { defaultValue: t('common.error_unknown') })}
          </Alert>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} aria-label={t('decisao.title')}>
          <fieldset className="cf-field" role="radiogroup" aria-label={t('decisao.kind_label')}>
            <legend className="cf-field__label">{t('decisao.kind_label')}</legend>
            {(['manter', 'reformar', 'reformar_parcial'] as const).map((k) => (
              <label
                key={k}
                style={{ display: 'flex', gap: 10, padding: '8px 0', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="kind"
                  value={k}
                  checked={kind === k}
                  onChange={() => setKind(k)}
                />
                <span>{t(`parecer.votos.${k}`)}</span>
              </label>
            ))}
          </fieldset>

          <Field label={t('decisao.justificativa_label')}>
            {({ id }) => (
              <Textarea
                id={id}
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                required
                minLength={50}
              />
            )}
          </Field>

          <Field
            label={t('decisao.carta_denunciante_label')}
            help={t('decisao.carta_denunciante_help')}
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                value={carta}
                onChange={(e) => setCarta(e.target.value)}
                required
              />
            )}
          </Field>
          <div style={{ marginTop: -8, marginBottom: 16 }}>
            <h2 style={{ marginTop: 4, fontSize: '0.95rem' }}>{t('decisao.preview_title')}</h2>
            <CartaRecursoPreview kind={kind} caseId={caseId} razao={carta} />
          </div>

          <Field
            label={t('decisao.carta_denunciado_label')}
            help={t('decisao.carta_denunciado_help')}
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                value={denunciado}
                onChange={(e) => setDenunciado(e.target.value)}
                required
              />
            )}
          </Field>

          <Button
            type="submit"
            loading={submitting}
            disabled={!quorumOk}
            title={!quorumOk ? t('decisao.errors.pareceres_insuficientes') : undefined}
          >
            {t('decisao.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
